import { type PromotionCodeErrorType } from '@repo/shared/types';
import { convertNumberToLocaleMoneyFormat } from '@repo/shared/helpers';
import { useCallback } from 'react';

import type { DiscountAppliedData } from '@/components/promotion-code/promotion-code-storage';
import { MODAL_STATE, useModalStore } from '@/components/modal/modal.store';

interface PromotionCodeCallbackOptions {
  onDiscountApplied?: (data: DiscountAppliedData, code: string) => void;
  onDiscountCleared?: () => void;
}

interface PromotionCodeErrorPayload {
  errorType: PromotionCodeErrorType;
  amount?: number;
  data?: DiscountAppliedData;
  code?: string;
}

export const usePromotionCodeCallback = ({
  onDiscountApplied,
  onDiscountCleared,
}: PromotionCodeCallbackOptions = {}) => {
  const { openModal } = useModalStore();

  const handlePromotionCodeCallback = useCallback(
    (reasonCode: PromotionCodeErrorType, data?: DiscountAppliedData, code?: string) => {
      switch (reasonCode) {
        case 'INVALID_OFFER':
          if (data && code) onDiscountApplied?.(data, code);
          console.warn('INVALID_OFFER', data);
          break;
        case 'PRICING_TOO_LOW':
          if (data && code) onDiscountApplied?.(data, code);
          break;
        case 'LOAN_AMOUNT_TO_HIGH':
          if (data && code) onDiscountApplied?.(data, code);
          break;
        case 'LOAN_AMOUNT_TO_LOW':
          if (data && code) onDiscountApplied?.(data, code);
          break;
        case 'NEXT_LOAN':
        case 'FIRST_LOAN':
        case 'USED':
        case 'CODE_EXPIRED':
        case 'CODE_NOT_YET_ENABLED':
        case 'INACTIVE':
        case 'INVALID_CODE':
          onDiscountCleared?.();
          break;
      }
    },
    [onDiscountApplied, onDiscountCleared],
  );

  const handlePromotionCodeError = useCallback(
    ({ errorType, amount, data, code }: PromotionCodeErrorPayload) => {
      openModal(MODAL_STATE.PROMOTION_CODE_ERROR, undefined, {
        errorType,
        amount: amount !== undefined ? convertNumberToLocaleMoneyFormat(amount) : undefined,
        onClose: () => handlePromotionCodeCallback(errorType, data, code),
      });
    },
    [handlePromotionCodeCallback, openModal],
  );

  return { handlePromotionCodeError };
};
